import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import CartItemsInCart from "../canvas/CartItemsInCart";
import cartAPI from "../../api/cartApi";
import Breadcrump from "../../components/Breadcrump";
import { showError, showSuccess } from "../../utils/toastUtils";
import Summary from "./common/Summary";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { FiShoppingBag, FiArrowRight, FiTag } from "react-icons/fi";

function Cart() {
  const { cartItems, fetchCart, cartSummary } = useCart();

  const [couponList, setCouponList] = useState([]);
  const [couponCode, setCouponCode] = useState(cartSummary?.couponCode || "");
  const [activeCoupon, setActiveCoupon] = useState(cartSummary?.couponCode || "");

  useEffect(() => {
    fetchCouponCodes();
  }, []);

  useEffect(() => {
    setCouponCode(cartSummary?.couponCode || "");
    setActiveCoupon(cartSummary?.couponCode || "");
  }, [cartSummary]);

  const fetchCouponCodes = async () => {
    try {
      const res = await cartAPI.showCoupon();
      if (res.code === 200) {
        setCouponList(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const applyCoupon = async (codeParam) => {
    const codeToApply = codeParam || couponCode;
    setActiveCoupon(codeToApply);
    setCouponCode(codeToApply);

    try {
      const res = await cartAPI.applyCoupon({ couponCode: codeToApply });
      if (res.code === 200) {
        await fetchCart();
        showSuccess("Coupon applied successfully");
      } else {
        setActiveCoupon("");
        showError(res.message || "Invalid coupon code");
      }
    } catch (err) {
      setActiveCoupon("");
      handleApiValidationError(err, showError);
    }
  };

  const removeCoupon = async () => {
    try {
      const res = await cartAPI.removeCoupon();
      if (res.code === 200) {
        setActiveCoupon("");
        setCouponCode("");
        fetchCart();
        showSuccess("Coupon removed");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <Breadcrump title="Your Shopping Bag" />

      <div className="cart-page-main py-5">
        <div className="container">
          {cartItems && cartItems.length > 0 ? (
            <div className="row g-5">
              
              {/* LEFT SIDE: PRODUCTS TABLE & COUPONS */}
              <div className="col-xl-8">
                <div className="table-responsive funky-table-wrapper">
                  <table className="funky-cart-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>MRP</th>
                        <th>Price</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* FIXED: 'cartProduct' namespace to prevent variable collision */}
                      {cartItems.map((cartProduct) => (
                        <CartItemsInCart key={cartProduct._id} item={cartProduct} />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* COUPON INPUT AREA */}
                <div className="coupon-section-wrapper mt-5">
                  <h5 className="funky-sub-heading mb-3">
                    <FiTag className="me-2" /> Have a Coupon?
                  </h5>
                  <div className="coupon-input-group">
                    <input 
                      type="text"
                      className="funky-input"
                      placeholder="Enter Code..."
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                    />
                    <button
                      type="button"
                      className={`funky-coupon-btn ${activeCoupon ? 'btn-remove' : 'btn-apply'}`}
                      onClick={activeCoupon ? removeCoupon : () => applyCoupon()}
                    >
                      {activeCoupon ? "REMOVE" : "APPLY"}
                    </button>
                  </div>

                  {/* TICKETS GRID LIST */}
                  {couponList && couponList.length > 0 && (
                    <div className="row g-3 mt-3">
                      {/* FIXED: Explicit couponItem binding */}
                      {couponList.map((couponItem) => (
                        <div className="col-md-6" key={couponItem._id}>
                          <div className={`coupon-ticket-card ${activeCoupon === couponItem.code ? 'active' : ''}`}>
                            <div className="ticket-left">
                              <span className="discount-val">
                                {couponItem?.discountType === "PERCENTAGE" 
                                  ? `${couponItem?.discountValue}%` 
                                  : `₹${couponItem?.discountValue}`}
                              </span>
                              <small>OFF</small>
                            </div>
                            
                            <div className="coupon-cart-divider"></div>
                            
                            <div className="ticket-right">
                              <h6>{couponItem?.title}</h6>
                              <code>{couponItem?.code}</code>
                              <button 
                                type="button" 
                                onClick={() => applyCoupon(couponItem.code)}
                                disabled={activeCoupon === couponItem.code}
                              >
                                {activeCoupon === couponItem.code ? "Applied" : "Apply Now"}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT SIDE: SIDEBAR ORDER SUMMARY */}
              <div className="col-xl-4">
                <div className="order-summary-sidebar">
                  <h4 className="summary-title">ORDER SUMMARY</h4>
                  <div className="summary-card-inner">
                    <Summary cartItems={cartItems} cartSummary={cartSummary} />
                    <Link to="/checkout" className="checkout-funky-btn mt-4">
                      PROCEED TO CHECKOUT <FiArrowRight className="ms-2" />
                    </Link>
                  </div>
                  <div className="trust-badge mt-3">
                    <FiShoppingBag className="me-2" /> Secure Checkout & Fast Delivery
                  </div>
                </div>
              </div>

            </div>
          ) : (
            /* EMPTY VIEW FOR CART CANVAS */
            <div className="empty-cart-state text-center py-5">
              <div className="empty-vibe-icon mb-4">
                <FiShoppingBag size={60} />
              </div>
              <h2 className="mb-3">YOUR BAG IS EMPTY!</h2>
              <p className="mb-4">Looks like you haven't added any premium items yet.</p>
              <Link to="/shop" className="shop-now-funky">
                START SHOPPING ⚡
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Cart;


